/**
 * Header chip that opens a small popover mapping the technical DATA(Q)
 * terms used in the Transform UI to plain market-research equivalents.
 *
 * Chosen over inline relabelling because the technical names (QTable,
 * Field, FFIELD, etc.) appear in the generated script and need to keep
 * matching what researchers see in the engine logs.
 */
import React from "react"
import { Button, Popover } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faBookOpen,
  faArrowUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons"
import { DOCS_LINKS } from "constants/docsLinks"

interface GlossaryEntry {
  term: string
  meaning: React.ReactNode
}

const GLOSSARY: GlossaryEntry[] = [
  {
    term: "Container / QTable",
    meaning: (
      <>
        A node in the project tree that <em>holds</em> other things —
        usually a multi-answer question (e.g. <code>Banks used</code>)
        or a banner (e.g. <code>Demographics banner</code>). Does not
        store data of its own.
      </>
    ),
  },
  {
    term: "Field",
    meaning: (
      <>
        A leaf in the project tree that <em>stores respondent data</em>{" "}
        — e.g. a single answer option (<code>Gender//Female</code>) or
        a numeric variable (<code>Age</code>). Yes/no fields are{" "}
        <code>BOOL</code>, numerics are <code>INT</code> or{" "}
        <code>REAL</code>, free text is <code>TEXT</code>.
      </>
    ),
  },
  {
    term: "Variable",
    meaning: (
      <>
        Everyday word for either a Field <em>or</em> the Container that
        groups its answer options. "Overall satisfaction" with its
        five-point scale is usually a Container; the "Very satisfied"
        leaf under it is a Field.
      </>
    ),
  },
  {
    term: "Path (//)",
    meaning: (
      <>
        Slash-slash separator for nested names.{" "}
        <code>Overall satisfaction//Satisfied</code> is the "Satisfied"
        answer option under the "Overall satisfaction" question. Names
        with spaces work — they're wrapped in{" "}
        <code>&lt;&lt;…&gt;&gt;</code> automatically.
      </>
    ),
  },
  {
    term: "NET",
    meaning: (
      <>
        A derived yes/no field that combines several existing answers —
        e.g. Top-2-Box (Very Satisfied + Satisfied), Total Awareness
        (aware of any brand), Aware <em>and</em> Considered.
      </>
    ),
  },
  {
    term: "Universe / Filter",
    meaning: (
      <>
        <strong>Universe</strong> restricts a single table to a slice
        (e.g. only 18–34s). <strong>Filter (Conditions)</strong>{" "}
        is the compound version — multiple groups OR/AND'd into one
        boolean used as the table's universe.
      </>
    ),
  },
  {
    term: "Apply",
    meaning: (
      <>
        Runs the whole pipeline against the engine session, then refreshes
        the data tree. New Containers and Fields become available to all
        Builders and the Console after this.
      </>
    ),
  },
  {
    term: "FOR loop",
    meaning: (
      <>
        Repeats the inner steps once per value of an iteration variable —
        e.g. once per bank, once per country. The loop variable (a short
        uppercase name like <code>BANK</code> or <code>COUNTRY</code>)
        is substituted into field paths at run time.
      </>
    ),
  },
  {
    term: "DEFINE",
    meaning: (
      <>
        Names a value so later steps can refer to it by that name. Pure
        text substitution — useful for long path prefixes that show up
        in many steps.
      </>
    ),
  },
  {
    term: "APPENDSTRUCT",
    meaning: (
      <>
        Copies the <em>shape</em> of one Container (fields + sub-containers,
        no data) into another. Inside a FOR loop with a per-iteration
        alias, it materialises one sub-tree per loop value.
      </>
    ),
  },
]

const GlossaryButton: React.FC = () => (
  <Popover
    placement="bottomRight"
    trigger="click"
    overlayClassName="qc-glossary-overlay"
    content={
      <div className="qc-glossary">
        <p className="qc-glossary__intro">
          Quick translation between the Transform pipeline's technical
          terms and their market-research equivalents.
        </p>
        <div className="qc-glossary__docs-link" style={{ margin: "0 0 10px" }}>
          <Button
            type="link"
            size="small"
            style={{ padding: 0, height: "auto", fontSize: 12 }}
            onClick={() =>
              window.open(DOCS_LINKS.QUERY_LANGUAGE, "_blank")
            }
          >
            <FontAwesomeIcon
              icon={faArrowUpRightFromSquare}
              style={{ marginRight: 6 }}
            />
            Full query language docs
          </Button>
        </div>
        <dl className="qc-glossary__list">
          {GLOSSARY.map((entry) => (
            <React.Fragment key={entry.term}>
              <dt>{entry.term}</dt>
              <dd>{entry.meaning}</dd>
            </React.Fragment>
          ))}
        </dl>
      </div>
    }
  >
    <Button
      aria-label="Open glossary of survey-research term equivalents"
      icon={<FontAwesomeIcon icon={faBookOpen} />}
    >
      Glossary
    </Button>
  </Popover>
)

export default GlossaryButton
